import React, { useState, useRef } from 'react';
import { Upload, FileText, X, Plus, ChevronRight } from 'lucide-react';
import { Input } from './ui/input';

const MultiFileUpload = ({
  files,
  onFilesChange,
  fileInputId,
  accept,
  emptyIcon: EmptyIcon = Upload,
  emptyTitle = "Upload Your File",
  emptySubtitle = "Upload a file to get started",
  emptyButtonText = "Browse Files",
  emptyNote = "CAD files, images, PDFs supported",
  renderPreview,
  required = false,
}) => {
  const [fileMode, setFileMode] = useState('single');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const addMoreRef = useRef(null);

  const handleFileUpload = (event) => {
    const newFiles = Array.from(event.target.files || []);
    if (newFiles.length === 0) return;

    if (fileMode === 'single') {
      onFilesChange(newFiles.slice(0, 1));
      setSelectedIndex(0);
    } else {
      const merged = [...files, ...newFiles];
      onFilesChange(merged);
      setSelectedIndex(merged.length - 1);
    }
    // Reset input so re-uploading same file triggers change
    event.target.value = '';
  };

  const handleAddMore = (event) => {
    const newFiles = Array.from(event.target.files || []);
    if (newFiles.length === 0) return;
    const merged = [...files, ...newFiles];
    onFilesChange(merged);
    setSelectedIndex(merged.length - 1);
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    const updated = files.filter((_, i) => i !== index);
    onFilesChange(updated);
    if (selectedIndex >= updated.length) {
      setSelectedIndex(Math.max(0, updated.length - 1));
    }
  };

  const handleModeChange = (mode) => {
    setFileMode(mode);
    if (mode === 'single' && files.length > 1) {
      onFilesChange([files[selectedIndex] || files[0]]);
      setSelectedIndex(0);
    }
  };

  const activeFile = files[selectedIndex] || null;

  return (
    <div className="mb-8 pb-8 border-b border-slate-200">
      {/* File mode toggle */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm font-medium text-slate-700">Upload:</span>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => handleModeChange('single')}
            className={`px-4 py-1.5 text-sm font-medium transition-colors ${
              fileMode === 'single'
                ? 'bg-[#0007b5] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Single file
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('multiple')}
            className={`px-4 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
              fileMode === 'multiple'
                ? 'bg-[#0007b5] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Multiple files
          </button>
        </div>
      </div>

      {/* Upload area */}
      <div className={`relative rounded-2xl transition-all duration-300 overflow-hidden ${
        files.length > 0
          ? 'bg-slate-50 border-2 border-dashed border-slate-300'
          : 'bg-slate-50 border-2 border-dashed border-slate-300 hover:border-[#0007b5]'
      }`}>
        {files.length > 0 ? (
          <div className="p-6">
            {/* File header / selection pane */}
            {fileMode === 'multiple' && files.length > 1 ? (
              <div className="mb-4 pb-4 border-b border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-slate-700">{files.length} files uploaded</span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => addMoreRef.current?.click()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0007b5] text-white rounded-lg hover:bg-[#000690] text-xs font-medium transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add More
                    </button>
                    <button
                      type="button"
                      onClick={() => document.getElementById(fileInputId).click()}
                      className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-xs font-medium transition-colors"
                    >
                      Replace All
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {files.map((file, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      className={`group relative flex items-center gap-2 pl-3 pr-7 py-2 rounded-lg text-left text-sm transition-all ${
                        selectedIndex === index
                          ? 'bg-[#0007b5] text-white shadow-md'
                          : 'bg-white border border-slate-200 text-slate-700 hover:border-[#0007b5]'
                      }`}
                    >
                      <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate max-w-[120px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemoveFile(index); }}
                        className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors ${
                          selectedIndex === index
                            ? 'hover:bg-white/20 text-white/80'
                            : 'hover:bg-slate-200 text-slate-400'
                        }`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0007b5] rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 truncate max-w-xs">{activeFile?.name}</p>
                    <p className="text-xs text-slate-500">
                      {activeFile ? (activeFile.size / 1024 / 1024).toFixed(2) : 0} MB
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {fileMode === 'multiple' && (
                    <button
                      type="button"
                      onClick={() => addMoreRef.current?.click()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0007b5] text-white rounded-lg hover:bg-[#000690] text-xs font-medium transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add More
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => document.getElementById(fileInputId).click()}
                    className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
                  >
                    Change File
                  </button>
                </div>
              </div>
            )}

            {/* Preview area */}
            {activeFile && renderPreview(activeFile)}
          </div>
        ) : (
          <div
            className="p-12 text-center cursor-pointer"
            onClick={() => document.getElementById(fileInputId).click()}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-200 flex items-center justify-center">
              <EmptyIcon className="w-10 h-10 text-slate-400" />
            </div>
            <p className="text-xl font-semibold text-slate-900 mb-2">
              {emptyTitle}
            </p>
            <p className="text-slate-500 mb-6">
              {emptySubtitle}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById(fileInputId).click();
              }}
              className="inline-flex items-center justify-center px-8 py-4 bg-[#0007b5] text-white rounded-xl hover:bg-[#0007b5]/90 transition-colors font-semibold shadow-lg shadow-[#0007b5]/20 text-lg"
            >
              <Upload className="w-5 h-5 mr-2" />
              {emptyButtonText}
            </button>
            <p className="text-slate-400 mt-4 text-sm">
              {emptyNote}
            </p>
          </div>
        )}

        {/* Hidden file inputs */}
        <Input
          id={fileInputId}
          type="file"
          accept={accept}
          multiple={fileMode === 'multiple'}
          onChange={handleFileUpload}
          className="hidden"
        />
        <input
          ref={addMoreRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleAddMore}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default MultiFileUpload;
